import Pagination from "./Pagination";
import { Loader2, Inbox } from "lucide-react";

/**
 * Generic paginated table.
 * Props:
 *  - columns: [{ key, header, render?: (row) => node, className?: string }]
 *  - rows: array of row objects
 *  - meta: { page, limit, total, total_pages }
 *  - loading, error
 *  - onPageChange(nextPage)
 *  - testid (prefix)
 */
export default function AnalyticsTable({
  columns,
  rows,
  meta,
  loading,
  error,
  onPageChange,
  testid = "analytics-table",
  emptyText = "لا توجد سجلات في هذه الفترة.",
}) {
  return (
    <div
      data-testid={testid}
      className="bg-white border-2 border-slate-200 rounded-2xl overflow-hidden"
    >
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}
      {!loading && error && (
        <div
          data-testid={`${testid}-error`}
          className="p-6 text-sm text-red-800 bg-red-50 border-t-2 border-red-200"
        >
          {error}
        </div>
      )}
      {!loading && !error && (!rows || rows.length === 0) && (
        <div
          data-testid={`${testid}-empty`}
          className="flex flex-col items-center justify-center py-16 text-slate-500"
        >
          <Inbox className="w-8 h-8 mb-2 text-slate-300" />
          <span className="text-sm">{emptyText}</span>
        </div>
      )}
      {!loading && !error && rows && rows.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b-2 border-slate-100">
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      className={`px-4 py-3 text-right font-bold text-slate-600 ${c.className || ""}`}
                    >
                      {c.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={row._id || row.event_id || row.call_id || i}
                    data-testid={`${testid}-row-${i}`}
                    className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60"
                  >
                    {columns.map((c) => (
                      <td key={c.key} className={`px-4 py-3 text-slate-700 ${c.className || ""}`}>
                        {c.render ? c.render(row) : row[c.key] ?? "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={meta?.page || 1}
            totalPages={meta?.total_pages || 1}
            total={meta?.total || 0}
            onChange={onPageChange}
            testid={`${testid}-pagination`}
          />
        </>
      )}
    </div>
  );
}
